/**
 * Matches loadjs load-type prefixes (`css!`, `img!`, `module!`, `nomodule!`)
 * at the start of a path.
 */
const LOADJS_PREFIX_REGEX = /^(css|img|module|nomodule)!/;

/**
 * Marks `.mjs` files so loadjs loads them as ES modules.
 *
 * loadjs 4.3.0 loads a script with `type="module"` when its path is prefixed
 * with `module!`. Rather than requiring callers to use that prefix, we follow
 * the convention that files with the `.mjs` extension are ES modules and add
 * the prefix automatically. Files that should load as modules can be renamed
 * to use the `.mjs` extension. Paths that already carry a loadjs prefix are
 * left unchanged.
 *
 * @param paths - The file paths to be passed to loadjs
 * @returns The paths with `module!` prepended to `.mjs` files
 * @example
 * ```typescript
 * markEsModulePaths(['/vendor/chart.mjs', '/vendor/legacy.js']);
 * // Returns ['module!/vendor/chart.mjs', '/vendor/legacy.js']
 * ```
 * @see https://github.com/kubetail-org/loadjs#documentation
 * @since 5.0.19
 */
export function markEsModulePaths(paths: string[]): string[] {
  return paths.map(path => {
    // ignore query strings and hashes when checking the extension, as loadjs
    // does when it detects the file type
    const pathname = path.replace(/[?#].*$/, '');
    if (!LOADJS_PREFIX_REGEX.test(path) && pathname.toLowerCase().endsWith('.mjs')) {
      return `module!${path}`;
    }
    return path;
  });
}
